import { useLocation, useNavigate } from "react-router-dom";
import "./SurveyResultDetailPage.scss";
import Buttons from "../../../components/Buttons/Buttons";
import { BackArrow } from "../../../assets/icons-components/Icons";
import { safeParseSessionStorage } from "../../../utils/storage";
import { downloadJSON } from "../../../utils/downloadFile";
import { onKeyActivate } from "../../../utils/a11y";

const InfoField = ({ label, value }) => (
  <div className="survey-result-detail_info-field">
    <span className="survey-result-detail_info-field_label">{label}</span>
    <span className="survey-result-detail_info-field_value">{value}</span>
  </div>
);

export default function SurveyResultDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const result = location?.state?.result || safeParseSessionStorage("surveyResultRowData");
  // Falls back to sessionStorage so the back button still returns to the
  // right survey's results (not the surveys list) after a page refresh,
  // which drops router state — see useSurveyResults' handleView.
  const resultsContext = safeParseSessionStorage("surveyResultsContext") || {};
  const surveyId = location?.state?.surveyId ?? resultsContext.surveyId;
  const surveyTitle = location?.state?.surveyTitle ?? resultsContext.surveyTitle;
  const from = location?.state?.from ?? resultsContext.from;

  // Opened from a patient's Survey tab (see SurveyTab's handleView) rather
  // than a survey's own results list — return there, reopened on the
  // Survey tab, instead of the survey-scoped results list.
  const handleBack = () =>
    from === "patient-survey"
      ? navigate("/patient-view", { state: { activeTab: 4 } })
      : navigate("/survey-results", { state: { surveyId, surveyTitle } });

  if (!result) {
    return (
      <div className="main1">
        <div className="main1_header" style={{ height: "auto", minHeight: "40px", flexWrap: "wrap", rowGap: "8px" }}>
          <div className="main1_header_left">
            <div
              className="main1_header_left_arrow"
              onClick={handleBack}
              onKeyDown={onKeyActivate(handleBack)}
              role="button"
              tabIndex={0}
            >
              <BackArrow onClick={handleBack} />
              <h3>Survey Result</h3>
            </div>
          </div>
        </div>
        <p style={{ padding: "20px" }}>No result selected.</p>
      </div>
    );
  }

  return (
    <div className="main1">
      <div className="main1_header" style={{ height: "auto", minHeight: "40px", flexWrap: "wrap", rowGap: "8px" }}>
        <div className="main1_header_left" style={{ width: "auto", flex: "1 1 200px" }}>
          <div
            className="main1_header_left_arrow"
            onClick={handleBack}
            onKeyDown={onKeyActivate(handleBack)}
            role="button"
            tabIndex={0}
          >
            <BackArrow onClick={handleBack} />
            <h3>{result.surveyTitle}</h3>
          </div>
        </div>
        <Buttons
          text="Download JSON"
          type="button"
          variant="primary"
          fullWidth={false}
          onClick={() => downloadJSON(`survey-result-${result.id}.json`, result)}
          style={{ height: 40, flexShrink: 0 }}
        />
      </div>

      <div className="survey-result-detail_card">
        <div className="survey-result-detail_info-grid">
          <InfoField label="Patient" value={result.patientName} />
          {result.surveyVersion != null && (
            <InfoField label="Survey Version" value={result.surveyVersion} />
          )}
          {result.assignedAt && (
            <InfoField label="Assigned" value={new Date(result.assignedAt).toLocaleString()} />
          )}
          <InfoField label="Submitted" value={new Date(result.submittedAt).toLocaleString()} />
        </div>

        <div className="survey-result-detail_divider">
          <h4>Answers</h4>
        </div>

        <div className="survey-result-detail_answers">
          {result.answers.map((answer) => (
            <div key={answer.questionId} className="survey-result-detail_answer">
              <div className="survey-result-detail_answer_question">{answer.questionText}</div>
              <div className="survey-result-detail_answer_value">
                {answer.answer === "" || answer.answer == null ? "-" : String(answer.answer)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
